import { SavedCompany } from "@/types/user";
import { Box } from "@/components/atoms";
import { Modal, Table } from "@/components/molecules";
import DeleteIcon from "@mui/icons-material/Delete";
import { Snackbar, Alert, IconButton } from "@mui/material";
import { useCompanyContext } from "@/providers/contexts";
import { useUpdateSavedCompany, useUpdateSavedCompanyViewCount } from "@/hooks";
import { useEffect, useState } from "react";
import { TextField } from "@mui/material";
import { State } from "@/providers/UserProvider";

const columns: {
  key: keyof SavedCompany | "notes" | "saved_reason";
  label: string;
}[] = [
  { key: "logo_url", label: "Logo" },
  { key: "company_name", label: "Company Name" },
  { key: "healthcare_domains", label: "Healthcare Domains" },
  { key: "saved_reason", label: "Reason" },
  { key: "notes", label: "Notes" },
];

interface FollowedCompaniesTableProps {
  savedCompanies: SavedCompany[];
  refetchUser: State["refetchUser"];
  isDisabled: boolean;
}
export const FollowedCompaniesTable = ({
  savedCompanies,
  refetchUser,
  isDisabled,
}: FollowedCompaniesTableProps) => {
  /* --------------------- Hooks --------------------- */
  const { setCompanyId, setCompanyModalOpen } = useCompanyContext();
  const { updateSavedCompanyViewCount } = useUpdateSavedCompanyViewCount();
  const {
    updateSavedCompany,
    error: updateError,
    data: updateData,
    loading,
  } = useUpdateSavedCompany();

  const [activeCompany, setActiveCompany] = useState<SavedCompany | null>(null);
  const [reasonText, setReasonText] = useState("");
  const [reasonModalOpen, setReasonModalOpen] = useState(false);

  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteText, setNoteText] = useState("");

  const [snackBar, setSnackBar] = useState<{
    state: boolean;
    msg: string;
    severity: "success" | "error";
  }>({ state: false, msg: "", severity: "success" });

  /* --------------------- Handles --------------------- */

  const handleNoteClick = (company: SavedCompany) => {
    setActiveCompany(company);
    setNoteText(company.notes || "");
    setNoteModalOpen(true);
  };

  const handleReasonClick = (company: SavedCompany) => {
    setActiveCompany(company);
    setReasonText(company.saved_reason || "");
    setReasonModalOpen(true);
  };

  const handleSaveNote = async () => {
    if (!activeCompany) return;
    await updateSavedCompany(activeCompany.id, { notes: noteText });
    setNoteModalOpen(false);
    setActiveCompany(null);
  };

  const handleSaveReason = async () => {
    if (!activeCompany) return;
    await updateSavedCompany(activeCompany.id, { saved_reason: reasonText });
    setReasonModalOpen(false);
    setActiveCompany(null);
  };

  const handleDeleteNote = async () => {
    if (!activeCompany) return;
    await updateSavedCompany(activeCompany.id, { notes: null });
    setNoteModalOpen(false);
    setActiveCompany(null);
  };

  const handleDeleteReason = async () => {
    if (!activeCompany) return;
    await updateSavedCompany(activeCompany.id, { saved_reason: null });
    setReasonModalOpen(false);
    setActiveCompany(null);
  };

  const handleCloseReasonModal = () => {
    setReasonModalOpen(false);
    setActiveCompany(null);
  };

  const handleCloseNoteModal = () => {
    setNoteModalOpen(false);
    setActiveCompany(null);
  };

  const handleCompanyClick = (company: SavedCompany) => {
    setCompanyId(company.company_id);
    setCompanyModalOpen(true);
    updateSavedCompanyViewCount({ saved_company_id: company.id });
  };

  useEffect(() => {
    if (updateData?.success) {
      setSnackBar({
        state: true,
        msg: updateData.message || "Note saved successfully.",
        severity: "success",
      });

      setTimeout(() => {
        refetchUser();
      }, 1000);
    }
  }, [updateData, refetchUser]);

  useEffect(() => {
    if (updateError) {
      setSnackBar({
        state: true,
        msg: updateError,
        severity: "error",
      });
    }
  }, [updateError]);

  return (
    <Box
      componentName="followed-companies-table"
      border={false}
      sx={{ flex: 2, mt: 2 }}
    >
      <Snackbar
        open={snackBar.state}
        autoHideDuration={4000}
        onClose={() => setSnackBar(prev => ({ ...prev, state: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackBar.severity}
          variant="filled"
          onClose={() => setSnackBar(prev => ({ ...prev, state: false }))}
        >
          {snackBar.msg}
        </Alert>
      </Snackbar>

      {activeCompany && noteModalOpen && (
        <Modal
          componentName="followed-company-note"
          header={`Notes for ${activeCompany?.company_name}`}
          open={noteModalOpen}
          renderButtonSet
          handleRightButtonClick={handleSaveNote}
          rightButtonText="Save Note"
          onCloseButtonClick={handleCloseNoteModal}
          leftButtonText="Cancel"
          disableRightButton={noteText.trim() === "" || loading}
        >
          <Box
            componentName="followed-company-note-row"
            border={false}
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            display="flex"
            sx={{ mt: 2 }}
          >
            <TextField
              label="Note"
              multiline
              value={noteText}
              fullWidth
              onChange={e => setNoteText(e.target.value)}
              helperText="Add a note to help you remember important details about this company."
            />
            <IconButton
              onClick={handleDeleteNote}
              size="large"
              disabled={noteText.trim() === "" || loading}
              sx={{
                cursor:
                  noteText.trim() === "" || loading ? "not-allowed" : "pointer",
                opacity: noteText.trim() === "" || loading ? 0.5 : 1,
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Modal>
      )}

      {activeCompany && reasonModalOpen && (
        <Modal
          componentName="followed-company-saved-reason"
          header={`Saved Reason for ${activeCompany?.company_name}`}
          subheader={
            "Please help us improve your experience by providing a reason for saving this company."
          }
          open={reasonModalOpen}
          renderButtonSet
          handleRightButtonClick={handleSaveReason}
          rightButtonText="Save"
          onCloseButtonClick={handleCloseReasonModal}
          leftButtonText="Cancel"
          disableRightButton={reasonText.trim() === "" || loading}
        >
          <Box
            componentName="followed-company-saved-reason-row"
            border={false}
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            display="flex"
            sx={{ mt: 2 }}
          >
            <TextField
              label="Reason for saving this company"
              multiline
              fullWidth
              value={reasonText}
              onChange={e => setReasonText(e.target.value)}
              helperText="Add a reason to help remember why you saved this company or what stood out to you."
            />
            <IconButton
              onClick={handleDeleteReason}
              size="large"
              disabled={reasonText.trim() === "" || loading}
              sx={{
                cursor:
                  reasonText.trim() === "" || loading
                    ? "not-allowed"
                    : "pointer",
                opacity: reasonText.trim() === "" || loading ? 0.5 : 1,
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Modal>
      )}

      <Table
        componentName={"followed-companies"}
        title="Followed Companies"
        columns={columns}
        rows={savedCompanies}
        onCompanyNameClick={handleCompanyClick}
        onNoteClick={handleNoteClick}
        onSavedReasonClick={handleReasonClick}
        isDisabled={isDisabled}
      />
    </Box>
  );
};
